import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeftIcon, ArrowRightIcon, BanknotesIcon, CalendarDaysIcon, ChatBubbleLeftEllipsisIcon, CheckIcon, EnvelopeIcon, FolderOpenIcon, ExclamationCircleIcon, InformationCircleIcon, PhoneIcon, UserPlusIcon, XMarkIcon,
} from '@heroicons/react/24/outline'
import useDocumentTitle from '../hooks/useDocumentTitle'
import { BramkaTresci } from '../components/auth/BramkaLogowania'
import * as api from '../api/rynek'
import { createKlient, dodajNotatke } from '../api/account'
import { stanDlaFirmy, terminKrotko, useCoMinute } from '../components/rynek/RynekFirmy'
import { BEZ_UPRAWNIENIA_RYNKU, POWODY_ODMOWY } from '../config/rynek'
import { plural } from '../components/kits/format'
import ProfilNaRynku from '../components/rynek/ProfilNaRynku'
import SprzetZlecenia from '../components/rynek/SprzetZlecenia'
import TeczkaWykonawcy from '../components/teczka/TeczkaWykonawcy'
import { czasDoTerminu, kodOgloszenia, opisWyboru, wierszeOpisu, zl } from '../lib/rynek'
import WyborSprzetu from '../components/oferta/WyborSprzetu'
import OcenaCeny from '../components/rynek/OcenaCeny'
import OknoKalendarzaFirmy from '../components/rynek/OknoKalendarzaFirmy'
import RealizacjaZlecenia from '../components/rynek/RealizacjaZlecenia'

/**
 * Ogłoszenie z Rynku dla firmy (`/rynek/:id`, link z maila „Nowe ogłoszenie na Rynku”).
 * Odpowiedź: cena brutto, termin i wiadomość (`PUT /firma/ogloszenia/{id}/odpowiedz`), poprawki i wycofanie.
 * Kontakt Kowalskiego widać dopiero po jego wyborze albo udostępnieniu.
 * „Dodaj do moich klientów” działa od razu, także bez kontaktu (prośba właściciela 2026-10-21: zachęta do CRM): zakłada
 * kartę w CRM firmy (`POST /api/v1/account/klienci`, źródło `rynek`) z notatką o ogłoszeniu. Backend zna kartę po
 * `dane.rynek.ogloszenie` (`klient_id` w ogłoszeniu) i sam dopisuje do niej kontakt, gdy klient go udostępni.
 *
 * Mini przetarg (2026-10-24, `o.udzial`): najpierw zaproszenie z terminem i „Biorę udział” albo „Nie biorę udziału”
 * z powodem (`POST /firma/ogloszenia/{kod}/udzial`); ofertę firma dodaje dopiero po potwierdzeniu, a kontakt dostaje,
 * gdy klient ją wybierze.
 *
 * Nadzór Rynku (prośba właściciela 2026-11-04, `pages/RynekNadzorZlecenie.jsx`) pokazuje tę samą stronę (`SzczegolyZlecenia`)
 * z danymi z `GET /nadzor/{kod}`: kontekst `PodgladZlecenia` blokuje każdy zapis (oferta, odmowa, karta w CRM, umowa
 * i montaż), a formularz da się wypełnić jak u firmy.
 */

export const PodgladZlecenia = createContext(null)
export const PODGLAD_BEZ_ZAPISU = 'To podgląd z Nadzoru Rynku: nic nie zapisujemy ani nie wysyłamy.'

const poleKlasa = `h-[46px] w-full rounded-xl border-[1.5px] border-gray-300 bg-white px-3.5 text-[15px] text-gray-900
  placeholder:text-gray-400 focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-600/15`

/**
 * Drugie zlecenie tego samego klienta (prośba właściciela 2026-11-04): zwykła informacja, że to poprawiona wersja
 * wcześniejszego, z jego skrótem, a gdy firma dostała i tamto, z linkiem (backend `api._poprzednie_klienta`).
 */
const PoprzednieKlienta = ({ p }) => {
  const podglad = useContext(PodgladZlecenia)
  const dzien = p.created_at ? new Date(p.created_at).toLocaleDateString('pl-PL', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Warsaw' }) : ''
  const fakty = [p.miejscowosc, dzien, p.kwp && `${String(p.kwp).replace('.', ',')} kWp`,
    p.magazyn_kwh && `${String(p.magazyn_kwh).replace('.', ',')} kWh`].filter(Boolean).join(', ')
  return (
    <p className="m-0 mt-3 flex items-start gap-2 text-sm text-gray-700">
      <InformationCircleIcon className="mt-0.5 h-5 w-5 flex-none text-blue-600" aria-hidden="true" />
      <span>
        <strong className="text-gray-900">To {p.ktore}, poprawione zlecenie tego klienta.</strong>{' '}
        Poprzednie: {fakty}.{p.kod && !podglad && <> <Link to={`/rynek/${p.kod}`} className="font-semibold text-blue-700">Zobacz poprzednie</Link></>}
      </span>
    </p>
  )
}

const liczba = (v) => {
  const n = Number(String(v).replace(',', '.').replace(/\s/g, ''))
  return String(v).trim() && Number.isFinite(n) && n > 0 ? n : null
}

/** Karta klienta w CRM z ogłoszenia: dane z kontaktu, adres bez ulicy, moc i magazyn z opisu. */
export const kartaZOgloszenia = (o) => {
  const k = o.kontakt || {}
  const nazwa = [k.imie, k.nazwisko].filter(Boolean).join(' ') || `Klient z Rynku, ${o.miejscowosc}`
  return {
    nazwa,
    zrodlo: 'rynek',
    dane: {
      imie: k.imie || '', nazwisko: k.nazwisko || '', telefon: k.telefon || '', email: k.email || '',
      adres: { wojewodztwo: o.wojewodztwo_nazwa || '', powiat: o.powiat || '', miejscowosc: o.miejscowosc || '' },
      mocPv: o.opis?.kwp ? String(o.opis.kwp).replace('.', ',') : '',
      pojemnosc: o.opis?.magazyn_kwh ? String(o.opis.magazyn_kwh).replace('.', ',') : '',
      rynek: { ogloszenie: o.id, komentarz: o.komentarz || '' },
    },
  }
}

/** Notatka w karcie klienta: co klient ogłosił, bez danych osobowych, z linkiem do ogłoszenia. */
export const notatkaZOgloszenia = (o) => [
  `Z Rynku ofert: ${o.typ_nazwa}, ${[o.miejscowosc, o.powiat, o.wojewodztwo_nazwa].filter(Boolean).join(', ')}.`,
  wierszeOpisu(o.opis).map(([e, w]) => `${e}: ${w}`).join('; '),
  [o.termin_nazwa, o.zuzycie_kwh ? `zużycie ${o.zuzycie_kwh} kWh rocznie` : '', o.budzet_brutto ? `budżet ${zl(o.budzet_brutto)}` : '',
    o.pme2 ? 'chce dotację PME2' : ''].filter(Boolean).join(', '),
  o.komentarz ? `Komentarz klienta: ${o.komentarz}` : '',
  `Ogłoszenie: ${window.location.origin}/rynek/${kodOgloszenia(o)}`,
].filter(Boolean).join('\n')

const KartaWCrm = ({ o, onKarta }) => {
  const podglad = useContext(PodgladZlecenia)
  const [busy, setBusy] = useState(false)
  const [blad, setBlad] = useState('')

  const dodaj = async () => {
    if (podglad) { setBlad(PODGLAD_BEZ_ZAPISU); return }
    setBusy(true)
    setBlad('')
    const r = await createKlient(kartaZOgloszenia(o))
    if (r.ok) await dodajNotatke(r.data.id, notatkaZOgloszenia(o))
    setBusy(false)
    if (!r.ok) { setBlad(r.error); return }
    onKarta(r.data.id)
  }

  if (o.klient_id) {
    return (
      <div className="flex flex-wrap items-center gap-3">
        <Link to={`/klienci/${o.klient_id}`} className="inline-flex h-[46px] items-center gap-2 rounded-xl border-[1.5px] border-gray-300 bg-white px-4 text-sm font-semibold text-gray-900 no-underline hover:border-gray-400">
          Karta klienta<ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
        </Link>
        <span className="text-sm text-gray-600">Klient jest w Twoich klientach{o.kontakt ? '.' : ', kontakt dopisze się sam, gdy go udostępni.'}</span>
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" disabled={busy} onClick={dodaj}
                className={`inline-flex h-[46px] items-center gap-2 rounded-xl px-4 text-sm font-semibold disabled:opacity-60 ${o.kontakt
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'border-[1.5px] border-gray-300 bg-white text-gray-900 hover:border-gray-400'}`}>
          <UserPlusIcon className="h-5 w-5" aria-hidden="true" />Dodaj do moich klientów
        </button>
        {!o.kontakt && <span className="text-sm text-gray-600">Etap, notatki i termin montażu w jednym miejscu. Kontakt dopisze się sam, gdy klient go udostępni.</span>}
      </div>
      {blad && <p role="alert" className="m-0 text-sm text-red-700">{blad}</p>}
    </div>
  )
}

const Formularz = ({ o, onZapisano }) => {
  const podglad = useContext(PodgladZlecenia)
  const odp = o.moja_odpowiedz
  const [cena, setCena] = useState(odp?.cena_brutto != null ? String(odp.cena_brutto) : '')
  const [termin, setTermin] = useState(odp?.termin_montazu || '')
  const [wiadomosc, setWiadomosc] = useState(odp?.status === 'wycofana' ? '' : (odp?.wiadomosc || ''))
  const [busy, setBusy] = useState(false)
  const [blad, setBlad] = useState('')
  const [zapisano, setZapisano] = useState(false)
  const aktywna = odp && odp.status !== 'wycofana'

  const wyslij = async (e) => {
    e.preventDefault()
    setBlad('')
    setZapisano(false)
    if (!wiadomosc.trim()) { setBlad('Napisz krótką wiadomość do klienta.'); return }
    if (podglad) { setBlad(PODGLAD_BEZ_ZAPISU); return }
    setBusy(true)
    const r = await api.odpowiedzFirmy(kodOgloszenia(o), { cena_brutto: liczba(cena), termin_montazu: termin.trim(), wiadomosc: wiadomosc.trim() })
    setBusy(false)
    if (!r.ok) { setBlad(r.error); return }
    setZapisano(true)
    onZapisano(r.data)
  }

  const wycofaj = async () => {
    if (podglad) { setBlad(PODGLAD_BEZ_ZAPISU); return }
    setBusy(true)
    const r = await api.wycofajOdpowiedz(kodOgloszenia(o))
    setBusy(false)
    if (r.ok) onZapisano(r.data)
    else setBlad(r.error)
  }

  return (
    <form onSubmit={wyslij} className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5 text-[13.5px] font-semibold text-gray-800">Cena brutto [zł]
          <input inputMode="numeric" value={cena} onChange={(e) => setCena(e.target.value)} placeholder="np. 48900" className={poleKlasa} />
        </label>
        <label className="flex flex-col gap-1.5 text-[13.5px] font-semibold text-gray-800">Termin montażu
          <input value={termin} onChange={(e) => setTermin(e.target.value)} placeholder="np. w ciągu 3 tygodni" className={poleKlasa} />
        </label>
      </div>
      <label className="flex flex-col gap-1.5 text-[13.5px] font-semibold text-gray-800">Wiadomość do klienta
        <textarea rows={5} maxLength={3000} value={wiadomosc} onChange={(e) => setWiadomosc(e.target.value)}
                  placeholder="Co proponujesz: sprzęt, zakres prac, gwarancja, pomoc z dotacją."
                  className="w-full rounded-xl border-[1.5px] border-gray-300 bg-white px-3.5 py-3 text-[15px] font-normal text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-600/15" />
      </label>
      {blad && (
        <p role="alert" className="m-0 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />{blad}
        </p>
      )}
      {zapisano && <p role="status" className="m-0 text-sm font-semibold text-emerald-700">Zapisane. Klient zobaczy Twoją ofertę.</p>}
      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" disabled={busy}
                className="flex h-[48px] items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 text-base font-semibold text-white shadow-[0_6px_16px_rgba(37,99,235,0.28)] hover:bg-blue-700 disabled:opacity-60">
          {aktywna ? 'Zapisz zmiany' : 'Wyślij ofertę'}<ArrowRightIcon className="h-[18px] w-[18px]" strokeWidth={2.2} aria-hidden="true" />
        </button>
        {odp?.status === 'wyslana' && (
          <button type="button" disabled={busy} onClick={wycofaj} className="text-sm font-semibold text-gray-500 hover:text-red-700">
            Wycofaj ofertę
          </button>
        )}
      </div>
    </form>
  )
}

const ZAKRESY = [['sprzet', 'Sprzęt'], ['materialy', 'Materiały instalacyjne'], ['montaz', 'Montaż'], ['formalnosci', 'Formalności']]
const MONTAZ = [['wlasna_ekipa', 'Własna ekipa'], ['partner', 'Ekipa partnerska']]

const lataGwarancji = (n) => `${n} ${n === 1 ? 'rok' : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 10 || n % 100 >= 20) ? 'lata' : 'lat'}`

const RODZAJE_POZYCJI = [['moduly', 'Moduły'], ['falownik', 'Falownik'], ['magazyn', 'Magazyn'], ['inne', 'Inne']]
const nazwaRodzaju = (r) => RODZAJE_POZYCJI.find(([k]) => k === r)?.[1] || r

/**
 * Wzór zestawu do oferty przy `kto_dobiera=voltvalue` (backend `przetarg.zestaw_zlecenia`): sprzęt z migawki teczki,
 * a bez niej z opisu zlecenia. Ilość modułów z opisu, reszta po jednej sztuce.
 * Część zlecenia bez wskazanego modelu (prośba właściciela 2026-10-30) też ma wiersz: „Moduły: dowolne” z mocą całej
 * instalacji i preferencjami klienta (`dowolna`, `opisKlienta`), a model wpisuje wykonawca.
 */
const czesciZlecenia = (o) => {
  const zakres = String(o.opis?.zakres || '').toLowerCase()
  if (zakres) return ['moduly', 'falownik', 'magazyn'].filter((r) => zakres.includes({ moduly: 'modu', falownik: 'falownik', magazyn: 'magazyn' }[r]))
  return { pv: ['moduly', 'falownik'], pv_magazyn: ['moduly', 'falownik', 'magazyn'], magazyn: ['magazyn'] }[o.typ] || []
}
const pl = (v) => String(v).replace('.', ',')
const opisDowolnej = (o, rodzaj) => {
  const d = o.opis || {}
  const baza = {
    moduly: d.kwp ? `Dowolne, ${pl(d.kwp)} kWp całej instalacji` : 'Dowolne',
    falownik: d.falownik_kw ? `Dowolny, ${pl(d.falownik_kw)} kW` : 'Dowolny',
    magazyn: d.magazyn_kwh ? `Dowolny, ${pl(d.magazyn_kwh)} kWh` : 'Dowolny',
  }[rodzaj]
  const woli = [d[`${rodzaj}_preferencja`], d[`${rodzaj}_uwagi`]].filter(Boolean).join(', ')
  return woli ? `${baza}. Klient woli: ${woli}` : baza
}
export const wzorZestawu = (o) => {
  const sprzet = o.teczka?.sprzet || {}
  const czesci = czesciZlecenia(o)
  return ['moduly', 'falownik', 'magazyn'].map((rodzaj) => {
    const nazwa = String(sprzet[rodzaj]?.nazwa || o.opis?.[rodzaj] || '').trim()
    if (nazwa) return { rodzaj, wzor: nazwa, model: nazwa, ilosc: rodzaj === 'moduly' && o.opis?.liczba_modulow ? Number(o.opis.liczba_modulow) : 1, zamiennik_powod: '' }
    if (!czesci.includes(rodzaj)) return null
    return { rodzaj, wzor: '', dowolna: true, opisKlienta: opisDowolnej(o, rodzaj), model: '', ilosc: rodzaj === 'moduly' ? '' : 1, zamiennik_powod: '' }
  }).filter(Boolean)
}

const tenSam = (a, b) => String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase()
/** Pozycja z innym modelem niż we wzorze to zamiennik i wymaga powodu (backend `pozycje_oferty`). */
export const jestZamiennikiem = (p) => Boolean(p.wzor) && !tenSam(p.model, p.wzor)

/** Pozycje do formularza: zapisana oferta albo wzór zestawu. */
const pozycjeStartowe = (o, odp) => {
  const wzor = wzorZestawu(o)
  if (!odp?.pozycje?.length) return wzor
  const zapisane = odp.pozycje.map((p) => {
    const w = wzor.find((x) => x.rodzaj === p.rodzaj)
    return {
      rodzaj: p.rodzaj, model: p.model, ilosc: p.ilosc || 1, zamiennik_powod: p.zamiennik_powod || '',
      wzor: p.zgodna === false ? (p.zamiennik_za || '') : (w?.wzor || ''), dowolna: w?.dowolna, opisKlienta: w?.opisKlienta,
    }
  })
  return [...zapisane, ...wzor.filter((w) => !odp.pozycje.some((p) => p.rodzaj === w.rodzaj))]
}

// Katalog w „Sprzęt w ofercie” (prośba właściciela 2026-11-02): ten sam wybór co w innych narzędziach. Pozycja „inne” zostaje
// wolnym tekstem, bo w katalogu takich rzeczy nie ma.
const KATALOG_POZYCJI = {
  moduly: { kategoria: 'panel', jednostka: 'W', etykietaMocy: 'Moc', placeholder: 'np. JA Solar JAM60D41-500' },
  falownik: { kategoria: 'inwerter', jednostka: 'kW', etykietaMocy: 'Moc', placeholder: 'np. Deye SUN-8K-SG05LP3' },
  magazyn: { kategoria: 'magazyn', jednostka: 'kWh', etykietaMocy: 'Pojemność', placeholder: 'np. Felicity FLA48460' },
}
const TEKSTY_KATALOGU = {
  albo: 'albo wybierz producenta, serię i model',
  wybrany: 'Ten model wpisaliśmy do oferty. Zmiana na listach go usunie.',
}

/**
 * „Sprzęt w ofercie” przy zestawie dobranym przez VoltValue: wzór klienta, zamiana modelu z powodem, pozycje „inne”.
 * Model wybiera się z katalogu albo wpisuje ręcznie, gdy sprzętu w nim nie ma; do oferty idzie tekst z pola „Model”.
 * Katalog jest otwarty tam, gdzie klient nie wskazał modelu, a przy wskazanym dopiero po kliknięciu, żeby nie wydłużać formularza.
 */
const SprzetWOfercie = ({ pozycje, onZmiana, blad }) => {
  // Z aktualnego stanu, nie z domknięcia: wybór z katalogu dopisuje serię po chwili, gdy dociągnie opis modelu.
  const zmienWiersz = (i, zmiana) => onZmiana((obecne) => obecne.map((p, j) => (j === i ? { ...p, ...zmiana } : p)))
  const zmien = (i, pole, v) => zmienWiersz(i, { [pole]: v })
  // Tekst wpisany ręcznie odpina wybór z katalogu, żeby listy nie pokazywały czegoś innego niż pole „Model”.
  const wpiszModel = (i, v) => zmienWiersz(i, { model: v, wybor: null })
  const wybierzZKatalogu = (i, p) => (w) => zmienWiersz(i, { wybor: w, model: w ? opisWyboru(w, KATALOG_POZYCJI[p.rodzaj].jednostka) || '' : (p.wzor || '') })
  return (
    <fieldset className="m-0 flex flex-col gap-3 border-0 p-0">
      <legend className="mb-1 text-[13.5px] font-semibold text-gray-800">Sprzęt w ofercie</legend>
      <p className="m-0 text-[13px] text-gray-600">
        Sprzęt wskazany w zleceniu. Możesz zmienić model, ale klient zobaczy zamiennik razem z powodem. Tam, gdzie klient
        nie wskazał modelu, wybierz z katalogu albo wpisz, co proponujesz.
      </p>
      {pozycje.map((p, i) => {
        const katalog = KATALOG_POZYCJI[p.rodzaj]
        const otwarty = katalog && (p.katalogOtwarty ?? Boolean(p.dowolna))
        return (
        <div key={i} className="flex flex-col gap-2 rounded-xl border border-gray-200 p-3">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="text-sm font-semibold text-gray-900">{nazwaRodzaju(p.rodzaj)}</span>
            {p.wzor ? <span className="text-[12.5px] text-gray-500">W zestawie klienta: {p.wzor}</span>
              : p.dowolna ? <span className="text-[12.5px] text-gray-500">{p.opisKlienta}</span>
              : <button type="button" onClick={() => onZmiana(pozycje.filter((_, j) => j !== i))} className="text-[13px] font-semibold text-gray-500 hover:text-red-700">Usuń</button>}
          </div>
          {katalog && (
            <button type="button" aria-expanded={Boolean(otwarty)} onClick={() => zmien(i, 'katalogOtwarty', !otwarty)}
                    className="self-start text-[13px] font-semibold text-blue-700 hover:underline">
              {otwarty ? 'Zwiń katalog' : 'Wybierz z katalogu: producent, seria, model'}
            </button>
          )}
          {otwarty && (
            <WyborSprzetu kategoria={katalog.kategoria} wartosc={p.wybor || null} onZmiana={wybierzZKatalogu(i, p)} placeholder={katalog.placeholder}
                          etykietaMocy={katalog.etykietaMocy} teksty={TEKSTY_KATALOGU} listaModeli />
          )}
          <div className="grid grid-cols-[minmax(0,1fr)_96px] gap-2">
            <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-gray-700">Model
              <input value={p.model} onChange={(e) => wpiszModel(i, e.target.value)} className={poleKlasa}
                     placeholder={p.dowolna ? 'Model, który proponujesz' : undefined} />
            </label>
            <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-gray-700">Ilość
              <input inputMode="numeric" value={p.ilosc} onChange={(e) => zmien(i, 'ilosc', e.target.value)} className={poleKlasa}
                     placeholder={p.dowolna && p.rodzaj === 'moduly' ? 'np. 12' : undefined} />
            </label>
          </div>
          {jestZamiennikiem(p) && (
            <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-gray-700">Dlaczego zamiennik?
              <input value={p.zamiennik_powod} maxLength={300} onChange={(e) => zmien(i, 'zamiennik_powod', e.target.value)}
                     placeholder="np. ten falownik jest dostępny od ręki i ma dłuższą gwarancję"
                     aria-invalid={Boolean(blad) && !p.zamiennik_powod.trim()} className={poleKlasa} />
            </label>
          )}
        </div>
        )
      })}
      <button type="button" onClick={() => onZmiana([...pozycje, { rodzaj: 'inne', model: '', ilosc: 1, zamiennik_powod: '', wzor: '' }])}
              className="self-start text-sm font-semibold text-blue-700 hover:underline">
        Dodaj pozycję
      </button>
      {blad && <p role="alert" className="m-0 text-sm text-red-700">{blad}</p>}
    </fieldset>
  )
}

/** Zakres ceny, który widzi wykonawca (90% do 105% budżetu klienta, bez samego budżetu, D15). */
export const zakresZlecenia = (o) => {
  const z = o.udzial?.zakres || { od: o.zakres_od, do: o.zakres_do }
  return z?.od && z?.do ? { od: Number(z.od), do: Number(z.do) } : null
}

/**
 * Oferta w wariancie D (kontrakt P0, decyzja D-e): oferta jest potwierdzeniem udziału, do terminu można ją poprawiać.
 * Cena suwakiem w zakresie klienta (pole obok przyjmuje też kwotę spoza zakresu: klient zobaczy, o ile go przekracza),
 * termin w tygodniach od umowy, gwarancja na montaż w latach, co jest w cenie i kto montuje.
 * Przy zestawie dobranym przez VoltValue także „Sprzęt w ofercie” (`pozycje`, zamiennik wymaga powodu).
 */
const FormularzOferty = ({ o, onZapisano }) => {
  const podglad = useContext(PodgladZlecenia)
  const odp =o.moja_odpowiedz && o.moja_odpowiedz.status !== 'wycofana' ? o.moja_odpowiedz : null
  const zakres = zakresZlecenia(o)
  // Punkt startowy: środek zakresu klienta. Zostaje zaznaczony na pasku po przesunięciu suwaka (prośba właściciela 2026-10-30).
  const punktStartowy = zakres ? Math.round((zakres.od + zakres.do) / 2 / 500) * 500 : null
  const start = odp?.cena_brutto ?? (punktStartowy ?? '')
  const [cena, setCena] = useState(start === '' ? '' : String(start))
  const [tygodnie, setTygodnie] = useState(odp?.termin_tygodni != null && !odp?.termin_od ? String(odp.termin_tygodni) : '')
  // Zamiast tygodni od umowy można podać wolny termin (prośba właściciela 2026-11-02); kalendarz firmy w przesuwanym oknie.
  const [zakresDat, setZakresDat] = useState(Boolean(odp?.termin_od))
  const [terminOd, setTerminOd] = useState(odp?.termin_od || '')
  const [terminDo, setTerminDo] = useState(odp?.termin_do || '')
  const [kalendarz, setKalendarz] = useState(false)
  const zamknijKalendarz = useCallback(() => setKalendarz(false), [])
  const wybierzZKalendarza = useCallback((od, doDnia) => { setZakresDat(true); setTerminOd(od); setTerminDo(doDnia) }, [])
  // Gwarancja na montaż z ankiety firmy, wpisana raz (decyzja właściciela 2026-10-25, uwagi przeglądu E9).
  const [gwarancja, setGwarancja] = useState(odp?.gwarancja_lat ?? null)
  // Bez gwarancji w ankiecie (opcjonalna od 2026-11-02) firma wpisuje ją tutaj, żeby ankieta nie blokowała oferty.
  const [gwarancjaZAnkiety, setGwarancjaZAnkiety] = useState(false)
  useEffect(() => {
    let anulowane = false
    // W podglądzie gwarancja z ankiety oglądanej firmy (backend `nadzor.widok_firmy`), nie z konta właściciela.
    const profil = podglad ? Promise.resolve({ ok: true, data: { gwarancja_lat: podglad.gwarancja_lat ?? null } }) : api.profilFirmy()
    profil.then((r) => {
      if (!anulowane && r.ok && r.data?.gwarancja_lat != null) { setGwarancja(r.data.gwarancja_lat); setGwarancjaZAnkiety(true) }
    })
    return () => { anulowane = true }
  }, [podglad])
  const [wCenie, setWCenie] = useState(odp?.zakres?.length ? odp.zakres
    : o.kto_kupuje === 'klient' ? ['materialy', 'montaz', 'formalnosci'] : ['sprzet', 'materialy', 'montaz', 'formalnosci'])
  const [montaz, setMontaz] = useState(odp?.montaz_wykona || '')
  const [wiadomosc, setWiadomosc] = useState(odp?.wiadomosc || '')
  const zZestawem = o.kto_dobiera === 'voltvalue'
  const [pozycje, setPozycje] = useState(() => (zZestawem ? pozycjeStartowe(o, odp) : []))
  const [bladPozycji, setBladPozycji] = useState('')
  const [busy, setBusy] = useState(false)
  const [blad, setBlad] = useState('')
  const [zapisano, setZapisano] = useState(false)
  const kwota = liczba(cena)
  const ponad = zakres && kwota && kwota > zakres.do ? kwota - zakres.do : 0

  const przelacz = (k) => setWCenie((z) => (z.includes(k) ? z.filter((x) => x !== k) : [...z, k]))

  const wyslij = async (e) => {
    e.preventDefault()
    setBlad('')
    setZapisano(false)
    if (!kwota) { setBlad('Podaj cenę brutto oferty.'); return }
    if (zakresDat && (!terminOd || !terminDo || terminOd > terminDo)) { setBlad('Podaj wolny termin montażu: od kiedy do kiedy.'); return }
    if (!zakresDat && !liczba(tygodnie)) { setBlad('Podaj termin montażu w tygodniach od umowy.'); return }
    if (gwarancja == null || gwarancja === '') { setBlad('Podaj gwarancję na montaż w latach.'); return }
    if (!wCenie.length) { setBlad('Zaznacz, co jest w cenie.'); return }
    if (!montaz) { setBlad('Wybierz, kto wykona montaż.'); return }
    if (!wiadomosc.trim()) { setBlad('Napisz krótko do klienta, co obejmuje oferta.'); return }
    setBladPozycji('')
    // Gdy sprzęt kupuje klient, wykonawca nie musi nazywać części, których klient nie wskazał.
    const pomijana = (p) => p.dowolna && o.kto_kupuje === 'klient' && !String(p.model).trim()
    if (zZestawem && pozycje.some((p) => !String(p.model).trim() && !pomijana(p))) { setBladPozycji('Podaj model w każdej pozycji.'); return }
    if (zZestawem && pozycje.some((p) => p.dowolna && p.rodzaj === 'moduly' && String(p.model).trim() && !liczba(p.ilosc))) { setBladPozycji('Podaj liczbę modułów.'); return }
    if (zZestawem && pozycje.some((p) => jestZamiennikiem(p) && !p.zamiennik_powod.trim())) {
      setBladPozycji('Przy zamienniku napisz klientowi, dlaczego zmieniasz model.')
      return
    }
    if (podglad) { setBlad(PODGLAD_BEZ_ZAPISU); return }
    setBusy(true)
    const r = await api.odpowiedzFirmy(kodOgloszenia(o), {
      cena_brutto: kwota, gwarancja_lat: Number(gwarancja),
      ...(zakresDat ? { termin_od: terminOd, termin_do: terminDo } : { termin_tygodni: Math.round(liczba(tygodnie)) }),
      zakres: wCenie, montaz_wykona: montaz, wiadomosc: wiadomosc.trim(),
      pozycje: zZestawem ? pozycje.filter((p) => String(p.model).trim()).map((p) => ({
        rodzaj: p.rodzaj, model: String(p.model).trim(), ilosc: Math.max(1, Math.round(liczba(p.ilosc) || 1)),
        ...(jestZamiennikiem(p) ? { zamiennik_powod: p.zamiennik_powod.trim() } : {}),
      })) : [],
    })
    setBusy(false)
    // Błąd pozycji (kod `pozycje`, `zamiennik_bez_powodu`) pokazujemy przy sekcji sprzętu, resztę pod formularzem.
    if (!r.ok && ['pozycje', 'zamiennik_bez_powodu'].includes(r.data?.kod)) { setBladPozycji(r.error); return }
    if (!r.ok) { setBlad(r.error); return }
    setZapisano(true)
    onZapisano(r.data)
  }

  const wycofaj = async () => {
    if (podglad) { setBlad(PODGLAD_BEZ_ZAPISU); return }
    setBusy(true)
    const r = await api.wycofajOdpowiedz(kodOgloszenia(o))
    setBusy(false)
    if (r.ok) onZapisano(r.data)
    else setBlad(r.error)
  }

  const naSuwaku = zakres ? Math.min(Math.max(kwota || zakres.od, zakres.od), zakres.do) : 0
  const procent = zakres ? ((naSuwaku - zakres.od) / Math.max(1, zakres.do - zakres.od)) * 100 : 0
  const procentStartu = zakres ? ((punktStartowy - zakres.od) / Math.max(1, zakres.do - zakres.od)) * 100 : 0
  // Środek uchwytu (26 px) przebiega od 13 px do szerokości minus 13 px, więc znacznik liczymy tak samo.
  const pozycjaStartu = `calc(${procentStartu}% + ${13 - (procentStartu / 100) * 26}px)`

  return (
    <form onSubmit={wyslij} className="flex flex-col gap-6" noValidate>
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <label htmlFor="oferta-cena" className="text-sm font-semibold text-gray-800">Cena brutto</label>
          <span className="flex items-center rounded-xl border-[1.5px] border-gray-300 bg-white focus-within:border-blue-600 focus-within:ring-4 focus-within:ring-blue-600/15">
            <input id="oferta-cena" inputMode="numeric" value={cena} onChange={(e) => setCena(e.target.value)} placeholder="np. 47900"
                   className="h-[46px] w-[130px] rounded-xl border-0 bg-transparent px-3.5 text-right text-lg font-bold tabular-nums text-gray-900 focus:outline-none focus:ring-0" />
            <span className="pr-3.5 text-sm font-semibold text-gray-500">zł</span>
          </span>
        </div>
        {zakres && (
          <div>
            <div className="relative">
              <input type="range" min={zakres.od} max={zakres.do} step={100} value={naSuwaku}
                     onChange={(e) => setCena(e.target.value)} aria-label="Cena w zakresie klienta" className="vv-range block"
                     style={{ background: `linear-gradient(to right, #1d4ed8 0%, #3b82f6 ${procent}%, rgb(var(--vv-bg-gray-200)) ${procent}%, rgb(var(--vv-bg-gray-200)) 100%)` }} />
              {naSuwaku !== punktStartowy && (
                <span aria-hidden="true" data-testid="punkt-startowy" style={{ left: pozycjaStartu }}
                      className="pointer-events-none absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-[3px] border-blue-600 bg-white" />
              )}
            </div>
            <div className="relative mt-1.5 flex justify-between text-[12px] tabular-nums text-gray-500">
              <span>{zl(zakres.od)}</span>
              <button type="button" onClick={() => setCena(String(punktStartowy))} style={{ left: pozycjaStartu }}
                      className="absolute top-0 -translate-x-1/2 whitespace-nowrap border-0 bg-transparent p-0 text-[12px] font-semibold tabular-nums text-blue-700 hover:underline">
                Start: {zl(punktStartowy)}
              </button>
              <span>{zl(zakres.do)}</span>
            </div>
          </div>
        )}
        <p className={`m-0 text-[13px] ${ponad ? 'font-semibold text-amber-700' : 'text-gray-600'}`}>
          {zakres
            ? (ponad ? `O ${zl(ponad)} ponad zakres klienta. Klient zobaczy, o ile oferta go przekracza.`
              : 'W zakresie, który przewidział klient.')
            : 'Klient nie podał budżetu.'}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5 text-sm font-semibold text-gray-800">
          <span>Termin montażu</span>
          <span className="flex items-center rounded-xl border-[1.5px] border-gray-300 bg-white focus-within:border-blue-600 focus-within:ring-4 focus-within:ring-blue-600/15">
            {zakresDat ? (
              <span className="flex min-w-0 flex-1 items-center gap-1 pl-2.5 text-[13px] font-normal text-gray-500">
                <input type="date" value={terminOd} onChange={(e) => setTerminOd(e.target.value)} aria-label="Wolny termin od"
                       className="h-[46px] w-full min-w-0 border-0 bg-transparent p-0 text-[14px] text-gray-900 focus:outline-none focus:ring-0" />
                do
                <input type="date" value={terminDo} min={terminOd || undefined} onChange={(e) => setTerminDo(e.target.value)} aria-label="Wolny termin do"
                       className="h-[46px] w-full min-w-0 border-0 bg-transparent p-0 text-[14px] text-gray-900 focus:outline-none focus:ring-0" />
              </span>
            ) : (
              <>
                <input inputMode="numeric" value={tygodnie} onChange={(e) => setTygodnie(e.target.value)} placeholder="np. 3" aria-label="Termin montażu w tygodniach od umowy"
                       className="h-[46px] w-full min-w-0 rounded-xl border-0 bg-transparent px-3.5 text-[15px] font-normal tabular-nums text-gray-900 focus:outline-none focus:ring-0" />
                <span className="whitespace-nowrap pr-2 text-[13px] font-medium text-gray-500">tyg. od umowy</span>
              </>
            )}
            <button type="button" onClick={() => setKalendarz((x) => !x)} aria-label="Pokaż mój kalendarz" aria-expanded={kalendarz} title="Pokaż mój kalendarz"
                    className="mr-1 inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg text-blue-700 hover:bg-gray-100">
              <CalendarDaysIcon className="h-5 w-5" aria-hidden="true" />
            </button>
          </span>
          <button type="button" onClick={() => setZakresDat((x) => !x)} className="self-start text-[13px] font-semibold text-blue-700 hover:underline">
            {zakresDat ? 'Podaj tygodnie od umowy' : 'Podaj wolny termin zamiast tygodni'}
          </button>
          {kalendarz && <OknoKalendarzaFirmy od={zakresDat ? terminOd : ''} do={zakresDat ? terminDo : ''} onWybierz={wybierzZKalendarza} onZamknij={zamknijKalendarz} />}
        </div>
        {gwarancjaZAnkiety ? (
          <div className="flex flex-col gap-1.5 text-sm font-semibold text-gray-800">
            <span>Gwarancja na montaż</span>
            <span className="flex min-h-[46px] flex-wrap items-center justify-between gap-2 rounded-xl border-[1.5px] border-gray-200 bg-gray-50 px-3.5 font-normal">
              <span className="text-[15px] text-gray-900">{lataGwarancji(gwarancja)}</span>
              <Link to="/firma/ankieta" className="text-[13px] font-semibold text-blue-700 hover:underline">Zmień w ankiecie</Link>
            </span>
          </div>
        ) : (
          <label className="flex flex-col gap-1.5 text-sm font-semibold text-gray-800">
            <span>Gwarancja na montaż (lata)</span>
            <input inputMode="numeric" value={gwarancja ?? ''} placeholder="np. 5" aria-label="Gwarancja na montaż w latach"
                   onChange={(e) => setGwarancja(e.target.value.replace(/\D/g, '').slice(0, 2))}
                   className="h-[46px] w-full rounded-xl border-[1.5px] border-gray-300 bg-white px-3.5 text-[15px] font-normal text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-600/15" />
          </label>
        )}
      </div>

      <fieldset className="m-0 border-0 p-0">
        <legend className="mb-2 text-sm font-semibold text-gray-800">W cenie</legend>
        <div className="flex flex-wrap gap-2">
          {ZAKRESY.map(([k, n]) => {
            const on = wCenie.includes(k)
            return (
              <button key={k} type="button" aria-pressed={on} onClick={() => przelacz(k)}
                      className={`inline-flex min-h-[40px] items-center gap-1.5 rounded-full border-[1.5px] px-3.5 text-[13.5px] transition
                                  ${on ? 'border-blue-600 bg-blue-600 font-semibold text-white' : 'border-gray-300 bg-white font-medium text-gray-700 hover:border-gray-400'}`}>
                {on && <CheckIcon className="h-3.5 w-3.5" strokeWidth={2.6} aria-hidden="true" />}{n}
              </button>
            )
          })}
        </div>
        {o.kto_kupuje === 'wykonawca' && <p className="m-0 mt-2 text-[13px] text-gray-600">Klient chce instalację ze sprzętem wykonawcy, więc sprzęt musi być w cenie.</p>}
      </fieldset>

      <fieldset className="m-0 border-0 p-0">
        <legend className="mb-2 text-sm font-semibold text-gray-800">Montaż wykona</legend>
        <div role="radiogroup" aria-label="Montaż wykona" className="inline-flex rounded-xl border border-gray-200 bg-gray-50 p-1">
          {MONTAZ.map(([k, n]) => (
            <button key={k} type="button" role="radio" aria-checked={montaz === k} onClick={() => setMontaz(k)}
                    className={`min-h-[38px] rounded-lg px-4 text-[13.5px] font-semibold transition ${montaz === k ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-600 hover:bg-white hover:text-blue-700'}`}>
              {n}
            </button>
          ))}
        </div>
      </fieldset>

      {zZestawem && <SprzetWOfercie pozycje={pozycje} onZmiana={setPozycje} blad={bladPozycji} />}
      <label className="flex flex-col gap-1.5 text-[13.5px] font-semibold text-gray-800">Wiadomość do klienta
        <textarea rows={4} maxLength={3000} value={wiadomosc} onChange={(e) => setWiadomosc(e.target.value)}
                  placeholder="Co obejmuje oferta: sprzęt, zakres prac, pomoc z dotacją."
                  className="w-full rounded-xl border-[1.5px] border-gray-300 bg-white px-3.5 py-3 text-[15px] font-normal text-gray-900 placeholder:text-gray-400 focus:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-600/15" />
      </label>
      {blad && (
        <p role="alert" className="m-0 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />{blad}
        </p>
      )}
      {zapisano && <p role="status" className="m-0 text-sm font-semibold text-emerald-700">Oferta zapisana. Do terminu możesz ją poprawiać.</p>}
      <div className="flex flex-wrap items-center gap-3">
        <button type="submit" disabled={busy}
                className="flex h-[48px] items-center justify-center gap-2 rounded-xl bg-blue-600 px-6 text-base font-semibold text-white shadow-[0_6px_16px_rgba(37,99,235,0.28)] hover:bg-blue-700 disabled:opacity-60">
          {odp ? 'Zapisz zmiany' : 'Złóż ofertę'}<ArrowRightIcon className="h-[18px] w-[18px]" strokeWidth={2.2} aria-hidden="true" />
        </button>
        {odp && (
          <button type="button" disabled={busy} onClick={wycofaj} className="text-sm font-semibold text-gray-500 hover:text-red-700">
            Wycofaj ofertę
          </button>
        )}
      </div>
    </form>
  )
}

/** Zaproszenie i stan udziału w wariancie D: odmowa z powodem przed ofertą, potem krótka informacja o wyniku. */
const Zaproszenie = ({ o }) => {
  const u = o.udzial

  const INFO = {
    oferta_zlozona: `Oferta złożona. Możesz ją poprawiać${u.termin ? ` do ${terminKrotko(u.termin)}` : ' do terminu'}. Potem dobierzemy najlepiej dopasowane oferty, a klient wybierze, kto dostanie jego kontakt.`,
    wygasla: 'Termin na złożenie oferty minął.',
    odmowila: 'Odmówiono udziału w tym zleceniu.',
    finalista: 'Twoja oferta przeszła dalej. Klient wybiera teraz, którzy wykonawcy dostaną jego kontakt.',
    nie_przeszla: `Twoja oferta nie przeszła dalej.${u.powod_wyniku ? ` ${u.powod_wyniku}` : ''} Dziękujemy za udział.`,
    niewybrana: 'Klient wybrał innych wykonawców. Dziękujemy za udział.',
    zamkniete: 'Klient zamknął to zlecenie.',
  }
  if (u.stan === 'wybrana') return null
  if (u.stan !== 'zaproszona') {
    return INFO[u.stan]
      ? <p role="status" className="m-0 rounded-[20px] border border-gray-200 bg-white p-5 text-sm leading-6 text-gray-700">{INFO[u.stan]}</p>
      : null
  }
  const zakres = zakresZlecenia(o)
  const czas = u.termin ? czasDoTerminu(u.termin) : null
  return (
    <section aria-labelledby="zaproszenie" className="rounded-[20px] border-2 border-blue-500 bg-white p-5 sm:p-6">
      <h2 id="zaproszenie" className="m-0 text-lg font-bold">Zaproszenie do zlecenia</h2>
      <p className="m-0 mt-1 text-sm leading-6 text-gray-700">
        Złóż ofertę{u.termin ? <> do <strong>{terminKrotko(u.termin)}</strong></> : null}: oferta jest zarazem potwierdzeniem
        udziału. Klient zobaczy {u.liczba_firm} najlepiej dopasowanych ofert i wybierze, kto dostanie jego adres i kontakt.
        {zakres ? <> Klient przewidział od <strong>{zl(zakres.od)}</strong> do <strong>{zl(zakres.do)}</strong>.</> : null}
      </p>
      {czas && (
        <p className="m-0 mt-2 flex items-center gap-1.5 text-sm font-semibold text-gray-800">
          <span className={`h-2.5 w-2.5 rounded-full ${czas.kolor}`} aria-hidden="true" />{czas.tekst[0].toUpperCase() + czas.tekst.slice(1)}
        </p>
      )}
      {zakres && <OcenaCeny o={o} zakres={zakres} zOpisem className="mt-2 text-sm" />}
    </section>
  )
}

/** „Nie biorę udziału” pod ofertą, na dole strony (prośba właściciela 2026-10-30): najpierw zlecenie i oferta, odmowa z powodem na końcu. */
const OdmowaUdzialu = ({ o, onZmiana }) => {
  const podglad = useContext(PodgladZlecenia)
  const [odmawiam, setOdmawiam] = useState(false)
  const [powod, setPowod] = useState('')
  const [opis, setOpis] = useState('')
  const [busy, setBusy] = useState(false)
  const [blad, setBlad] = useState('')

  const decyzja = async (dane) => {
    if (podglad) { setBlad(PODGLAD_BEZ_ZAPISU); return }
    setBusy(true)
    setBlad('')
    const r = await api.udzialFirmy(kodOgloszenia(o), dane)
    setBusy(false)
    if (r.ok) onZmiana(r.data)
    else setBlad(r.error)
  }

  return (
    <section aria-label="Odmowa udziału" className="flex flex-col gap-3">
      {blad && <p role="alert" className="m-0 text-sm text-red-700">{blad}</p>}
      {odmawiam ? (
        <div className="flex flex-col gap-3 rounded-[20px] border border-gray-200 bg-white p-5 sm:p-6">
          <div role="radiogroup" aria-label="Powód odmowy" className="flex flex-wrap gap-2">
            {POWODY_ODMOWY.map((p) => (
              <button key={p.klucz} type="button" role="radio" aria-checked={powod === p.klucz} onClick={() => setPowod(p.klucz)}
                      className={`min-h-[40px] rounded-full border-[1.5px] px-3.5 text-[13.5px] transition
                                  ${powod === p.klucz ? 'border-gray-900 bg-gray-900 font-semibold text-white' : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'}`}>
                {p.nazwa}
              </button>
            ))}
          </div>
          <label className="flex flex-col gap-1.5 text-[13.5px] font-semibold text-gray-800">
            <span>Dopisek <span className="font-normal text-gray-500">(opcjonalnie, widzi tylko VoltValue)</span></span>
            <input value={opis} maxLength={300} onChange={(e) => setOpis(e.target.value)} placeholder="np. pełny grafik do grudnia" className={poleKlasa} />
          </label>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" disabled={!powod || busy} onClick={() => decyzja({ decyzja: 'nie', powod, opis: opis.trim() })}
                    className="h-[44px] rounded-xl bg-red-600 px-4 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
              Odmów udziału
            </button>
            <button type="button" onClick={() => setOdmawiam(false)} className="h-[44px] px-3 text-sm font-semibold text-gray-600">Anuluj</button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          {/* E6: odmowa na czerwono. */}
          <button type="button" disabled={busy} onClick={() => setOdmawiam(true)}
                  className="inline-flex h-[44px] items-center gap-1.5 rounded-xl border-[1.5px] border-red-300 bg-white px-4 text-sm font-semibold text-red-700 hover:border-red-500 hover:bg-red-50 disabled:opacity-60">
            <XMarkIcon className="h-4 w-4" strokeWidth={2.4} aria-hidden="true" />Nie biorę udziału
          </button>
        </div>
      )}
    </section>
  )
}

/**
 * Teczka zlecenia u wykonawcy (uwagi przeglądu F6, 2026-10-25): przed ofertą podgląd bez adresu i mapy z PDF-em
 * (`TeczkaWykonawcy`, migawka `o.teczka` z backendu `teczka.prywatnosc.widok_migawki`); po kontakcie cała.
 */
const OknoTeczki = ({ o, onZamknij }) => {
  const zamknij = useRef(null)
  useEffect(() => {
    zamknij.current?.focus()
    const esc = (e) => { if (e.key === 'Escape') onZamknij() }
    document.addEventListener('keydown', esc)
    return () => document.removeEventListener('keydown', esc)
  }, [onZamknij])
  return (
    <div className="fixed inset-0 z-[70] flex items-end justify-center bg-gray-900/40 p-3 sm:items-center"
         onMouseDown={(e) => { if (e.target === e.currentTarget) onZamknij() }}>
      <div role="dialog" aria-modal="true" aria-labelledby="teczka-zlecenia"
           className="relative flex max-h-[calc(100dvh-1.5rem)] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl">
        <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
          <div>
            <h2 id="teczka-zlecenia" className="m-0 text-lg font-bold text-gray-900">Teczka instalacji</h2>
            <p className="m-0 text-[13px] text-gray-500">{o.kontakt ? 'Pełna teczka: klient przekazał Ci kontakt.' : 'Bez adresu i mapy. Adres dostaniesz z kontaktem.'}</p>
          </div>
          <button ref={zamknij} type="button" onClick={onZamknij} aria-label="Zamknij"
                  className="-mr-2 -mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100">
            <XMarkIcon className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4">
          <TeczkaWykonawcy teczka={o.teczka} wariant="wykonawca" poKontakcie={Boolean(o.kontakt) && !o.kontakt.ukryty} />
        </div>
      </div>
    </div>
  )
}

/**
 * Treść strony zlecenia u firmy. `pobierz` podmienia źródło danych (Nadzór Rynku: `api.nadzorZlecenie`); wtedy strona
 * nie gasi dzwonka, nie poprawia adresu i nie zapisuje niczego (kontekst `PodgladZlecenia` ustawia strona nadzoru).
 */
export const SzczegolyZlecenia = ({ id, pobierz }) => {
  useCoMinute()
  const podglad = useContext(PodgladZlecenia)
  const [teczka, setTeczka] = useState(false)
  const zamknijTeczke = useCallback(() => setTeczka(false), [])
  const navigate = useNavigate()
  const [o, setO] = useState(null)
  const [blad, setBlad] = useState('')
  const [brakAnkiety, setBrakAnkiety] = useState(false)
  const [bezUprawnienia, setBezUprawnienia] = useState(false)
  const [odswiez, setOdswiez] = useState(0)

  useEffect(() => {
    (pobierz || api.ogloszenieFirmy)(id).then((r) => {
      if (!r.ok) {
        setBrakAnkiety(r.data?.kod === 'brak_ankiety')
        setBezUprawnienia(r.data?.kod === 'bez_uprawnienia')
        setBlad(r.error)
        return
      }
      setBrakAnkiety(false)
      setBlad('')
      setO(r.data)
      if (pobierz) return
      // Otwarcie zlecenia gasi jego pozycje w dzwonku (backend `rynek/powiadomienia.oznacz_otwarte`).
      window.dispatchEvent(new Event('vv-powiadomienia-odswiez'))
      // Stary link z maila z liczbowym id: w pasku adresu zostaje kod.
      if (r.data.kod && r.data.kod !== id) navigate(`/rynek/${r.data.kod}`, { replace: true })
    })
  }, [id, navigate, odswiez, pobierz])

  // Odpowiedź z serwera po zapisie oferty niesie aktualne `klient_id`, więc karta zostaje po każdej zmianie.
  const ustawKarte = (klientId) => setO((x) => ({ ...x, klient_id: klientId }))

  // Bez ankiety firmy (2026-10-23) zlecenie otwiera się dopiero po jej zapisaniu.
  if (brakAnkiety) return <ProfilNaRynku onZapisano={() => setOdswiez((x) => x + 1)} />
  if (bezUprawnienia) return <p className="rounded-[18px] border border-gray-200 bg-white p-5 text-sm leading-6 text-gray-700">{BEZ_UPRAWNIENIA_RYNKU}</p>
  if (!o) return blad ? <p role="alert" className="text-sm text-red-700">{blad}</p> : <p className="text-sm text-gray-500">Wczytuję…</p>
  const s = stanDlaFirmy(o)
  const opis = wierszeOpisu(o.opis)
  // Wariant D: oferta jest potwierdzeniem udziału, poprawki do terminu; po wyniku zostaje tylko podgląd oferty.
  const wariantD = Boolean(o.udzial)
  const odpowiadac = o.otwarte && o.moja_odpowiedz?.status !== 'odrzucona' && !o.wybrano_nas
    && (!wariantD || ['zaproszona', 'oferta_zlozona'].includes(o.udzial.stan))
  const pokazOferte = !wariantD || !['odmowila', 'wygasla'].includes(o.udzial.stan)
  const mojaOferta = o.moja_odpowiedz

  return (
    <div className="flex flex-col gap-6">
      {/* Zaproszenie na górze: decyzja w 48 h to pierwszy ruch firmy w przetargu. */}
      {o.udzial?.stan === 'zaproszona' && <Zaproszenie o={o} />}
      <section className="rounded-[20px] border border-gray-200 bg-white p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="m-0 text-2xl font-bold">{o.typ_nazwa}</h1>
            <p className="m-0 mt-1 text-sm text-gray-500">{[o.miejscowosc, o.powiat, o.wojewodztwo_nazwa].filter(Boolean).join(', ')}</p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-gray-700">
            <span className={`h-2 w-2 rounded-full ${s.kolor}`} aria-hidden="true" />{s.nazwa}
          </span>
        </div>
        {o.poprzednie_klienta && <PoprzednieKlienta p={o.poprzednie_klienta} />}
        <SprzetZlecenia o={o} />
        {o.teczka && (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-2xl border border-blue-200 p-3.5">
            <span aria-hidden="true" className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-blue-600 text-white"><FolderOpenIcon className="h-5 w-5" /></span>
            <span className="min-w-0 flex-1 text-sm text-gray-700">
              <strong className="block text-gray-900">Teczka instalacji od klienta</strong>
              Rzut dachu, schemat, licznik i rozdzielnica. Wycenisz bez przyjazdu.
            </span>
            <button type="button" onClick={() => setTeczka(true)}
                    className="inline-flex h-[42px] items-center gap-1.5 rounded-xl bg-blue-600 px-4 text-sm font-semibold text-white hover:bg-blue-700">
              <FolderOpenIcon className="h-4 w-4" aria-hidden="true" />Otwórz teczkę
            </button>
          </div>
        )}
        {teczka && <OknoTeczki o={o} onZamknij={zamknijTeczke} />}
        {opis.length > 0 && !(o.opis?.kwp || o.opis?.falownik || o.opis?.magazyn_kwh || o.opis?.dach) && (
          <dl className="m-0 mt-4 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
            {opis.map(([e, w]) => (
              <div key={e}><dt className="text-[12.5px] text-gray-500">{e}</dt><dd className="m-0 text-sm font-semibold">{w}</dd></div>
            ))}
          </dl>
        )}
        {o.sprzet_nazwa && <p className="m-0 mt-3 text-sm font-semibold text-gray-800">{o.sprzet_nazwa}</p>}
        <p className="m-0 mt-3 text-sm text-gray-600">
          {o.termin_nazwa}
          {o.zuzycie_kwh ? ` · zużycie ${o.zuzycie_kwh.toLocaleString('pl-PL')} kWh rocznie` : ''}
          {o.budzet_brutto ? ` · budżet ${zl(o.budzet_brutto)}` : ''}
          {o.pme2 ? ' · chce dotację PME2' : ''}
        </p>
        {o.cena_oferty != null && (
          <p className="m-0 mt-2 text-sm text-gray-800">
            <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-amber-400 align-middle" aria-hidden="true" />
            Klient ma już ofertę za <strong>{zl(o.cena_oferty)}</strong> brutto.
          </p>
        )}
        {o.komentarz && (
          <div className="mt-4 flex items-start gap-3 rounded-2xl border border-blue-200 p-3.5">
            <span aria-hidden="true" className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-blue-600 text-white"><ChatBubbleLeftEllipsisIcon className="h-5 w-5" /></span>
            <span className="min-w-0 flex-1 text-sm text-gray-700">
              <strong className="block text-gray-900">Notatka od klienta</strong>
              <span className="whitespace-pre-line">{o.komentarz}</span>
            </span>
          </div>
        )}
        {!o.kontakt && <div className="mt-5 border-t border-gray-100 pt-4"><KartaWCrm o={o} onKarta={ustawKarte} /></div>}
      </section>

      {o.udzial && o.udzial.stan !== 'zaproszona' && <Zaproszenie o={o} />}

      {o.koniec_kontaktu && (
        <p role="status" className="m-0 rounded-[20px] border border-gray-300 bg-white p-5 text-sm leading-6 text-gray-700">
          Klient poprosił, żeby Twoja firma nie kontaktowała się już z nim w sprawie tego zlecenia. Jego dane zostały ukryte.
        </p>
      )}

      {o.kontakt && (
        <section aria-labelledby="kontakt" className="rounded-[20px] border-2 border-emerald-500 bg-white p-5 sm:p-6">
          <h2 id="kontakt" className="m-0 text-lg font-bold">Kontakt do klienta</h2>
          <p className="m-0 mt-1 text-sm text-gray-600">
            {o.wybrano_nas ? 'Klient wybrał Twoją ofertę.'
              : o.udzial?.stan === 'wybrana' ? 'Klient wybrał Twoją firmę.'
              : o.tryb_kontaktu === 'dopasowanie'
                ? 'Klient poprosił o dopasowanie firm: zadzwoń i dodaj ofertę poniżej, żeby mógł ją porównać.'
                : 'Klient udostępnił Ci kontakt.'}
          </p>
          {o.kontakt.ukryty ? (
            <p className="m-0 mt-3 text-sm text-gray-500">Imię, e-mail, telefon i adres klienta widzi tu firma. W podglądzie są tylko w panelu admina.</p>
          ) : (
          <div className="mt-3 flex flex-col gap-1.5 text-sm">
            {(o.kontakt.imie || o.kontakt.nazwisko) && <span className="font-semibold">{[o.kontakt.imie, o.kontakt.nazwisko].filter(Boolean).join(' ')}</span>}
            {(o.kontakt.ulica || o.kontakt.nr_domu) && (
              <span>{[[o.kontakt.ulica, o.kontakt.nr_domu].filter(Boolean).join(' '), o.kontakt.miejscowosc].filter(Boolean).join(', ')}</span>
            )}
            <a href={`mailto:${o.kontakt.email}`} className="inline-flex items-center gap-1.5 font-semibold text-blue-700 no-underline hover:underline">
              <EnvelopeIcon className="h-4 w-4" aria-hidden="true" />{o.kontakt.email}
            </a>
            {o.kontakt.telefon && (
              <a href={`tel:${o.kontakt.telefon.replace(/\s/g, '')}`} className="inline-flex items-center gap-1.5 font-semibold text-blue-700 no-underline hover:underline">
                <PhoneIcon className="h-4 w-4" aria-hidden="true" />{o.kontakt.telefon}
              </a>
            )}
          </div>
          )}
          <div className="mt-4"><KartaWCrm o={o} onKarta={ustawKarte} /></div>
        </section>
      )}

      {pokazOferte && (
      <section aria-labelledby="oferta" className="overflow-hidden rounded-[20px] border border-gray-200 bg-white">
        <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-4 sm:px-6">
          <span aria-hidden="true" className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white">
            <BanknotesIcon className="h-5 w-5" />
          </span>
          <h2 id="oferta" className="m-0 text-lg font-bold">Twoja oferta</h2>
          {o.udzial?.termin && ['zaproszona', 'oferta_zlozona'].includes(o.udzial.stan) && (
            <span className="ml-auto text-[13px] font-medium text-gray-500">do {terminKrotko(o.udzial.termin)}</span>
          )}
        </div>
        <div className="p-5 sm:p-6">
        {odpowiadac ? (
          <div>{wariantD ? <FormularzOferty o={o} onZapisano={setO} /> : <Formularz o={o} onZapisano={setO} />}</div>
        ) : (
          <div className="mt-3 text-sm text-gray-700">
            {mojaOferta ? (
              <>
                <p className="m-0">
                  {[mojaOferta.cena_brutto != null ? `${zl(mojaOferta.cena_brutto)} brutto` : 'Bez ceny',
                    mojaOferta.termin_od ? `wolny termin ${mojaOferta.termin_montazu}` : mojaOferta.termin_tygodni ? `montaż do ${mojaOferta.termin_tygodni} tyg. od umowy` : mojaOferta.termin_montazu,
                    mojaOferta.gwarancja_lat != null ? `gwarancja ${mojaOferta.gwarancja_lat} ${plural(mojaOferta.gwarancja_lat, 'rok', 'lata', 'lat')}` : null].filter(Boolean).join(' · ')}
                </p>
                <p className="m-0 mt-2 whitespace-pre-line">{mojaOferta.wiadomosc}</p>
              </>
            ) : <p className="m-0">Zlecenie jest zamknięte.</p>}
          </div>
        )}
        </div>
      </section>
      )}
      {o.udzial?.stan === 'zaproszona' && <OdmowaUdzialu o={o} onZmiana={setO} />}
      {/* Umowa i montaż (2026-11-03): na samym dole, bo przydaje się tygodnie po wyborze. */}
      {o.realizacja && (
        <RealizacjaZlecenia stan={o.realizacja} strona="firma" onZapisz={async (dane) => {
          if (podglad) return { ok: false, error: PODGLAD_BEZ_ZAPISU }
          const r = await api.realizacjaFirmy(kodOgloszenia(o), dane)
          if (r.ok) setO(r.data)
          return r
        }} />
      )}
    </div>
  )
}

const RynekOgloszenieFirmy = () => {
  const { id } = useParams()
  useDocumentTitle('Ogłoszenie na Rynku ofert')
  return (
    <div className="bg-gray-50 text-gray-900">
      <div className="mx-auto max-w-3xl px-4 pb-24 pt-8 sm:px-6 sm:pt-12">
        <Link to="/rynek" className="mb-5 inline-flex items-center gap-1.5 text-sm text-gray-500 no-underline hover:text-blue-700">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />Rynek ofert
        </Link>
        <BramkaTresci bramka="rynek" tryb="zamiast" tytul="Rynek ofert po zalogowaniu"
                      opis="Zaloguj się kontem firmy, żeby zobaczyć ogłoszenie i odpowiedzieć klientowi.">
          <SzczegolyZlecenia id={id} />
        </BramkaTresci>
      </div>
    </div>
  )
}

export default RynekOgloszenieFirmy
